import { formatAmount } from '@krp/ui-kit'

/**
 * Форматирование чисел и дат цен и импорта — ОДНА реализация на пакет
 * (CLAUDE.md §7: деньги одной утилитой, `toFixed` вне неё запрещён).
 */
const QUANTITY = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 3 })
const DATE_TIME = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})
const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })

/** Цена/сумма в рублях: «1 092,07 ₽». Пустое значение — прочерк, а не «0,00 ₽». Форматирует общая утилита ui-kit. */
export const formatMoney = (value: number | null | undefined): string => formatAmount({ rubles: value })

export function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return QUANTITY.format(value)
}

/** Календарная дата `YYYY-MM-DD` → «07.10.2026» без часового пояса (даты 1С — местные). */
export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return match === null ? value : `${match[3]}.${match[2]}.${match[1]}`
}

/** Момент времени ISO → «07.10.2026, 14:32» в часовом поясе человека. */
export function formatDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const moment = new Date(value)
  return Number.isNaN(moment.getTime()) ? value : DATE_TIME.format(moment)
}

/** Момент времени ISO → «14:32» в часовом поясе человека: для ответа, прочитанного только что. */
export function formatTime(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const moment = new Date(value)
  return Number.isNaN(moment.getTime()) ? value : TIME.format(moment)
}

/**
 * Число из поля ввода: запятая или точка как десятичный разделитель, пробелы
 * (в том числе неразрывные) игнорируются. Нераспознанное — `null`. Ввод идёт
 * через `InvariantCulture`-совместимую запись, а не `parseFloat` по локали.
 */
export function parseDecimal(input: string): number | null {
  const cleaned = input.replace(/\s/g, '').replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null
  const value = Number(cleaned)
  return Number.isFinite(value) ? value : null
}

/** Дробная часть не длиннее `places` знаков (сервер отвергает избыточную точность, а не округляет). */
export function hasAtMostDecimals(input: string, places: number): boolean {
  const cleaned = input.replace(/\s/g, '').replace(',', '.')
  const dot = cleaned.indexOf('.')
  return dot < 0 || cleaned.length - dot - 1 <= places
}

/** Канонический вид ключа 1С (нижний регистр, без фигурных скобок) или `null`, если это не GUID. */
export function normalizeGuid(input: string): string | null {
  const trimmed = input.trim().replace(/^\{|\}$/g, '').toLowerCase()
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(trimmed) &&
    trimmed !== '00000000-0000-0000-0000-000000000000'
    ? trimmed
    : null
}
